import React, { useEffect, useState, useRef, ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Tooltip,
  IconButton,
  Stack,
  Typography,
  CssBaseline,
  Theme,
  ToggleButton,
  Slide,
  ScopedCssBaseline,
  Alert,
  CircularProgress,
  AlertTitle,
} from '@mui/material/';
import List from '@mui/icons-material/List';

import ToggleButtonGroup, { toggleButtonGroupClasses } from '@mui/material/ToggleButtonGroup';

import Grid2 from '@mui/material/Unstable_Grid2';
import { useSnackbar } from 'notistack';
import {
  Lesson,
  TOCItem,
  LessonSegment,
  User,
  NextLessonSegmentForm,
  NextLessonSegmentResponse,
  GotoLessonInstanceResponse,
} from 'controllers/types';
import { createLessonInstance, gotoLessonSegment, nextLessonSegment } from 'controllers/lessonInstances';
import { styled, ThemeProvider } from '@mui/material/styles';
import { getTheme } from 'components/theme/kyronTheme';
import { FullScreen, FullScreenHandle, useFullScreenHandle } from 'react-full-screen';
import { ArrowBack as ArrowBackIcon, Chat as ChatIcon } from '@mui/icons-material';
import { VideoPlayer } from 'components/Player/VideoPlayer/VideoPlayer';
import { LessonSurvey } from 'components/Player/LessonSurvey';
import { TableOfContents } from 'components/Player/TableOfContents/TableOfContents';
import { QuestionAndStudentInputs } from 'components/Player/QuestionAndStudentInputs';
import { useMediaContainerResize } from 'components/Player/useMediaContainerResize';
import { kebabCase, startCase } from 'lodash';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { SvgIconTypeMap } from '@mui/material/SvgIcon/SvgIcon';
import { Transcripts } from 'components/Player/Transcripts/Transcripts';
import { PanelAreaProvider, usePanelAreaContext } from 'components/Player/PanelAreaContext';
import { GoogleClassroomStudentValidator } from 'components/GoogleClassroomStudentValidator';
import { KyronEvents } from '../utils/KyronEvents';
import { useClassroomCredentials } from '../ClassroomContext';
import { ClassroomCredentials, StudentEnrollmentStatus } from '../ClassroomLoader';
import { buildSegmentVideoUrlWithDefiniteEnding, buildSegmentVideoURL, getSearchParamStr } from '../utils/urlUtils';
import { useRemoveIntercom } from '../utils/KyronIntercom';

const BottomBarButtonNames = {
  TABLE_OF_CONTENTS: 'table_of_contents',
  TRANSCRIPT: 'transcript',
};

function isValidBottomBarButton(value: string): boolean {
  return Object.values(BottomBarButtonNames).indexOf(value) > -1;
}

const drawerWidth = 320;

const PlayerContainer = styled(Stack)(({ theme }) => ({
  position: 'absolute',
  overflow: 'hidden',
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  padding: theme.spacing(3),
  gap: theme.spacing(3),
}));

const MediaOuterContainer = styled(Box)`
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
`;

const MediaInnerContainer = styled(Box)`
  position: relative;
  overflow: hidden;
  border-radius: 20px;
`;

const MediaPlayerContainer = styled(Box)`
  position: relative;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
`;

const SlideContainer = styled(Slide)`
  width: ${drawerWidth}px;
  height: 100%;
  align-self: stretch;
  white-space: pre-wrap;
  word-wrap: break-word;
  transition: all 2s;
  '& .MuiDrawer-paper': {
    box-sizing: border-box;
  },
`;

const WaitSegementInputBarContainer = styled(Box, { shouldForwardProp: prop => prop !== 'open' })<{
  open?: boolean;
}>(({ open }) => ({
  display: 'grid',
  gridTemplateRows: '0fr',
  transition: 'grid-template-rows 300ms cubic-bezier(0.4, 0, 0.2, 1)',
  ...(open && {
    gridTemplateRows: '1fr',
    transition: 'grid-template-rows 300ms cubic-bezier(0.4, 0, 0.2, 1)',
  }),
}));

const BottomBar = styled(Grid2)`
  position: fixed;
  right: 24px;
  bottom: 24px;
`;

const RightBottomBar = styled(ToggleButtonGroup)(({ theme }) => ({
  position: 'absolute',
  right: 0,
  bottom: 0,
  display: 'flex',
  gap: theme.spacing(2),
  [`& .${toggleButtonGroupClasses.root}`]: {
    borderRadius: '24px',
  },
  [`& .${toggleButtonGroupClasses.grouped}`]: {
    border: 0,
    [`&.${toggleButtonGroupClasses.disabled}`]: {
      border: 0,
    },
    '&:not(:last-of-type)': {
      borderRadius: '100px',
    },
    '&:not(:first-of-type)': {
      borderRadius: '100px',
    },
  },
}));

const PlayerInternal = ({ user }: { user: User | null }) => {
  useRemoveIntercom();

  const { lessonId, segmentId } = useParams();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const ltiLaunch = search.get('lti_launch');
  const videoScreen = useFullScreenHandle();
  const queryParams = new URLSearchParams(window.location.search);
  let restart = queryParams.get('restart');
  if (!restart) {
    restart = 'false';
  }
  const [lessonName, setLessonName] = useState('');
  const [runningLessonSegment, setRunningLessonSegment] = useState<LessonSegment | null>(null);
  const [runningLessonSegmentInstanceId, setRunningLessonSegmentInstanceId] = useState(0);
  const [runningLessonSessionId, setRunningLessonSessionId] = useState(0);
  const [runningLessonInstanceId, setRunningLessonInstanceId] = useState(0);
  const [tableOfContents, setTableOfContents] = useState<TOCItem[]>([]);
  const [lesson, setLesson] = useState<Lesson | null>(null);
  const chatId = useRef(0);
  const [completed, setCompleted] = useState(false);
  const waitTimeoutId = useRef<NodeJS.Timeout | null>(null);
  const classroomCredentials = useClassroomCredentials();
  const { enqueueSnackbar } = useSnackbar();
  const [videoFileName, setVideoFileName] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const isWaitSegment = runningLessonSegment?.segment_type === 'wait';
  const [boxRef, { width, height }] = useMediaContainerResize();
  const { panelAreaOpen, setPanelAreaOpen, selectedPanelAreaButton, setSelectedPanelAreaButton } =
    usePanelAreaContext();

  function handleVideoSettings(lessonSegment: LessonSegment) {
    if (videoFileName !== lessonSegment.video_filename) {
      const urlWithParams = buildSegmentVideoUrlWithDefiniteEnding(lessonSegment);
      setVideoUrl(urlWithParams);
      setVideoFileName(lessonSegment.video_filename);
    }
  }
  function handleNextSegment() {
    const nextSegmentForm: NextLessonSegmentForm = {
      lesson_segment_instance_id: runningLessonSegmentInstanceId,
    };
    return handleNextLessonSegment(nextSegmentForm);
  }

  function handleFullScreen() {
    if (videoScreen.active) {
      videoScreen.exit();
    } else {
      videoScreen.enter();
    }
  }

  // fetch lesson data
  async function getLesson(id: number, classCreds: ClassroomCredentials) {
    if (!Number(id)) throw Error('Got an insufficient lesson ID to start the lesson');

    const isStudentEnrolled = classCreds?.status === StudentEnrollmentStatus.Enrolled;
    try {
      const response = await createLessonInstance(
        id,
        Number(segmentId),
        isStudentEnrolled ? Number(classCreds.classroomId) : 0,
        isStudentEnrolled ? 'Classroom' : '',
        restart !== 'false',
        ltiLaunch,
      );
      setRunningLessonSegment(response.lesson_segment);
      setRunningLessonSegmentInstanceId(response.lesson_segment_instance.id);
      setRunningLessonSessionId(Number(response.lesson_instance.session_id));
      setRunningLessonInstanceId(response.lesson_instance.id);
      setLessonName(response.lesson.name);
      setTableOfContents(response.table_of_contents);
      setLesson(response.lesson);
      chatId.current = response.chat_instance_id;
      setVideoUrl(
        buildSegmentVideoURL(
          response.lesson_segment.video_url,
          response.lesson_segment.start_time,
          response.lesson_segment.end_time,
        ),
      );

      // send events
      KyronEvents.resetOptions();
      KyronEvents.addOptions({
        user_id: user?.id || 'anonymous',
        lesson_id: response.lesson.id,
        lesson_name: response.lesson.name,
        creator_name: response.lesson.tutor?.display_name,
        lesson_instance_id: response.lesson_instance.id,
        session_id: response.lesson_instance.session_id,
        lti_launch: ltiLaunch,
      });
      KyronEvents.sendEvent('Start Lesson');
    } catch (error) {
      if (error instanceof Error) console.error('Player Lesson Start Error: ', error.message);
    }
  }

  useEffect(() => {
    if (!lessonId || !Number(lessonId)) return;
    getLesson(Number(lessonId), classroomCredentials);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId, classroomCredentials]);

  function handleLessonSegmentResponse(response: GotoLessonInstanceResponse | NextLessonSegmentResponse) {
    chatId.current = response.chat_instance_id;
    setRunningLessonSegmentInstanceId(response.lesson_segment_instance.id);
    const lessonSegment: LessonSegment = response.lesson_segment;
    setRunningLessonSegment(lessonSegment);
    if (lessonSegment.video_filename !== null) handleVideoSettings(lessonSegment);
  }
  async function handleTOCClick(tableOfContent: TOCItem) {
    try {
      const response: GotoLessonInstanceResponse = await gotoLessonSegment(runningLessonInstanceId, tableOfContent);
      handleLessonSegmentResponse(response);
    } catch (error) {
      if (error instanceof Error) console.error('StartLessonSegment Error: ', error.message);
    }
  }

  async function handleNextLessonSegment(lessonInstanceForm: NextLessonSegmentForm) {
    try {
      const response: NextLessonSegmentResponse = await nextLessonSegment(runningLessonInstanceId, lessonInstanceForm);
      if (response.completed) {
        /* this generates a DOM Error that the video is playing that
            we need to deal with */
        enqueueSnackbar('Lesson Completed. Congratulations.');
        KyronEvents.sendEvent('Completed Lesson');
        KyronEvents.resetOptions();
        setCompleted(true);
      } else {
        handleLessonSegmentResponse(response);
      }
      if (response.no_input_retry !== null) {
        enqueueSnackbar('No input detected. Please try again.', { variant: 'warning' });
      }
      return response;
    } catch (error) {
      if (error instanceof Error) enqueueSnackbar(error.message, { variant: 'error' });
    }
  }

  const changeLesson = (id: number) => {
    if (!id) {
      enqueueSnackbar('Cannot switch tutor: Lesson could not be found.');
      return;
    }
    // Get the new lesson
    getLesson(id, classroomCredentials);

    // Handle the state updates
    // -- Close the TOC
    setPanelAreaOpen(false);
    // -- Update lesson ID in URL
    // ---- NO a re-render
    // ---- NO new entry in history (so that back button goes to the right place)
    navigate(`/video_player/${id}${getSearchParamStr(search)}`, { replace: true });
  };

  function setWaitTimeout(handleWaitTimeout: () => void) {
    if (waitTimeoutId.current !== null) return;
    if (runningLessonSegment !== null && runningLessonSegment.wait_timeout > 0) {
      waitTimeoutId.current = setTimeout(() => handleWaitTimeout(), runningLessonSegment.wait_timeout * 1000);
    }
  }
  function cancelWaitTimeout() {
    if (waitTimeoutId.current !== null) {
      clearTimeout(waitTimeoutId.current);
      waitTimeoutId.current = null;
    }
  }

  // This method is used to calculate the width and height of the video player based
  // on the aspect ratio and the available space
  const resizeMediaContainerForAspectRatio = () => {
    // If the width and height ratio is less than 16/9, we need to compute the height based on the width.
    if (width / height < 16 / 9) {
      return {
        width: Math.floor(width / 16) * 16, // Math.floor is used to make sure that the width is a multiple of 16
        height: Math.floor(width / 16) * 9,
      };
    }
    // If the width and height ratio is greater than 16/9, we need to compute the width based on the height.
    return {
      width: Math.floor(height / 9) * 16,
      height: Math.floor(height / 9) * 9, // Math.floor is used to make sure that the height is a multiple of 9
    };
  };

  const feedbackForm = (
    <LessonSurvey
      sessionId={runningLessonSessionId?.toString() || ''}
      lesson={lesson}
      instanceId={runningLessonInstanceId?.toString() || ''}
      user={user}
    />
  );

  const getPanelButtons = (): { name: string; icon: OverridableComponent<SvgIconTypeMap>; hide?: boolean }[] => [
    {
      name: BottomBarButtonNames.TABLE_OF_CONTENTS,
      icon: List,
      hide: tableOfContents && tableOfContents.length === 0,
    },
    {
      name: BottomBarButtonNames.TRANSCRIPT,
      icon: ChatIcon,
    },
  ];

  const isLessonSegmentVideoError = (segment: LessonSegment) =>
    segment.video_url === null || segment.video_url.includes('status=error');
  const isLessonSegmentVideoProcessing = (segment: LessonSegment) =>
    segment.video_url === null || segment.video_url.includes('status=processing');

  const loadPanelAreaContent = (event: React.MouseEvent | null, value: string | null) => {
    if (value && isValidBottomBarButton(value)) {
      setSelectedPanelAreaButton(value);
      setPanelAreaOpen(true);
    } else {
      setSelectedPanelAreaButton(null);
      setPanelAreaOpen(false);
    }
  };

  const panelAreaTheme: Theme = getTheme('light');

  const renderSegmentVideoAlert = (lessonSegment: LessonSegment | null) => {
    if (!lessonSegment) return false;

    let alert: ReactNode | null = null;
    // check if any of the videos have some normalization error or process in progress
    // if so, we can't render the video player - this is mostly needed in the editor
    if (isLessonSegmentVideoProcessing(lessonSegment))
      alert = (
        <Alert severity='warning' icon={<CircularProgress size='1em' />}>
          <AlertTitle>Video is processing</AlertTitle>
          <Typography variant='bodyMedium'>
            This video is still being processed. Try playing again later. <br />
            This should not take more than a few minutes. If that is the case, <br />
            processing of this video might have failed unexpectedly.
          </Typography>
        </Alert>
      );

    if (isLessonSegmentVideoError(lessonSegment))
      alert = (
        <Alert severity='error'>
          <AlertTitle>Corrupted video</AlertTitle>
          <Typography variant='bodyMedium'>
            This video had an error during normalization process and is not suitable for playing.
          </Typography>
        </Alert>
      );

    return alert ? <Box style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>{alert}</Box> : null;
  };

  const segmentVideoAlert = renderSegmentVideoAlert(runningLessonSegment);

  return (
    <PlayerViewWrapper
      title={lesson?.lesson_collection?.name || lessonName}
      videoScreen={videoScreen}
      lessonCompleted={completed}
      feedbackForm={feedbackForm}
      hideNavigation={Boolean(ltiLaunch)} // LTI platforms don't want their users to navigate away from the page
    >
      {segmentVideoAlert}
      <Grid2 container sx={{ width: '100%', height: '100%', gap: 3 }}>
        <Grid2 xs>
          <Stack width='100%' height='100%' maxHeight={isWaitSegment ? '100%' : 'calc(100% - 48px)'}>
            <MediaOuterContainer ref={boxRef}>
              <Box sx={{ position: 'absolute', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <MediaInnerContainer style={resizeMediaContainerForAspectRatio()}>
                  {/* This box needs to be computed */}
                  <MediaPlayerContainer>
                    {runningLessonSegment && !segmentVideoAlert && (
                      <VideoPlayer
                        key={runningLessonSessionId} // very important to have - if missing, sometimes video player's state won't update
                        sessionId={runningLessonSessionId}
                        runningLessonSegment={runningLessonSegment}
                        segmentId={runningLessonSegmentInstanceId}
                        handleNextSegment={handleNextSegment}
                        setWaitTimeout={setWaitTimeout}
                        cancelWaitTimeout={cancelWaitTimeout}
                        handleFullScreen={handleFullScreen}
                        videoUrl={videoUrl}
                      />
                    )}
                  </MediaPlayerContainer>
                </MediaInnerContainer>
              </Box>
            </MediaOuterContainer>
            <Box>
              {/*
              Implemented css pattern mentioned
              here[https://keithjgrant.com/posts/2023/04/transitioning-to-height-auto/] for
              animating height transitions popping up for the wait segment input bar.
          */}
              <WaitSegementInputBarContainer open={isWaitSegment}>
                <Box overflow='hidden'>
                  <QuestionAndStudentInputs
                    lessonSegment={runningLessonSegment as never}
                    handleNextLessonSegment={handleNextLessonSegment}
                    chatId={chatId.current}
                    sessionId={runningLessonSessionId}
                    segmentInstanceId={runningLessonSegmentInstanceId}
                    cancelWaitTimeout={cancelWaitTimeout}
                  />
                </Box>
              </WaitSegementInputBarContainer>
            </Box>
          </Stack>
        </Grid2>
        <Grid2
          xs='auto'
          sx={{ pb: 8 }} // padding bottom to account for icons on the bottom right, like toc.
        >
          <ThemeProvider theme={panelAreaTheme}>
            <ScopedCssBaseline sx={{ height: '100%', borderRadius: '16px' }} enableColorScheme>
              <SlideContainer direction='left' in={panelAreaOpen} timeout={5} mountOnEnter unmountOnExit>
                <Box position='relative'>
                  <Box position='absolute' top={0} right={0} bottom={0} left={0}>
                    {selectedPanelAreaButton === BottomBarButtonNames.TABLE_OF_CONTENTS && (
                      <TableOfContents
                        tableOfContents={tableOfContents}
                        lesson={lesson!} // slide won't open if lesson is null, so lesson is defined here (note the mountOnEnter)
                        handleTOCItemClick={handleTOCClick}
                        changeLesson={changeLesson}
                        runningLessonSegmentId={runningLessonSegment?.id}
                      />
                    )}
                    {selectedPanelAreaButton === BottomBarButtonNames.TRANSCRIPT && (
                      <Transcripts transcript={runningLessonSegment?.transcript || ''} />
                    )}
                  </Box>
                </Box>
              </SlideContainer>
            </ScopedCssBaseline>
          </ThemeProvider>
        </Grid2>
      </Grid2>
      <BottomBar container>
        <RightBottomBar exclusive value={selectedPanelAreaButton} onChange={loadPanelAreaContent}>
          {getPanelButtons().reduce((accum: JSX.Element[], { name, icon: ButtonIcon, hide }) => {
            if (hide) {
              return accum;
            }
            accum.push(
              <ToggleButton
                key={name}
                value={name}
                data-testid={`${kebabCase(name)}-button`}
                aria-label={startCase(name)}
              >
                <Tooltip placement='top' title={startCase(name)}>
                  <ButtonIcon />
                </Tooltip>
              </ToggleButton>,
            );
            return accum;
          }, [])}
        </RightBottomBar>
      </BottomBar>
    </PlayerViewWrapper>
  );
};

type PlayerViewWrapperProps = {
  children: ReactNode;
  videoScreen: FullScreenHandle;
  title: string;
  lessonCompleted: boolean;
  feedbackForm: ReactNode;
  hideNavigation: boolean;
};

function PlayerViewWrapper({
  children,
  videoScreen,
  title,
  lessonCompleted,
  feedbackForm,
  hideNavigation,
}: PlayerViewWrapperProps) {
  const navigate = useNavigate();
  const theme: Theme = getTheme('dark');
  /**
   * window.history.state.idx: In some web frameworks (like React Router), idx is used to
   * keep track of the position in the history stack. A value greater than 0 implies that
   * there are previous entries in the history stack, meaning the user has navigated at
   * least once since opening the page. Hence, we are understanding whether the user has
   * navigated withing the Kyron app.
   */
  const hasInAppHistory = window.history.state.idx > 0;

  /**
   * After a redirect, if user wants to go back to the previous page by clicking the back button, they will immediately
   * be redirected to back they were. Because, whatever the page they were previously is redirecting them here. So, we
   * shouldn't show the back button in this case and leave it to the browser's back button.
   */
  // This rule blocks me from getting `_isRedirect` from `window.history.state.usr`
  // eslint-disable-next-line no-underscore-dangle
  const isInitialRedirection = window.history.state.usr?._isRedirect;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline enableColorScheme>
        <FullScreen handle={videoScreen}>
          <PlayerContainer>
            <Stack direction='row' alignItems='center'>
              {!hideNavigation && hasInAppHistory && !videoScreen.active && !isInitialRedirection && (
                <IconButton onClick={() => navigate(-1)} sx={{ mr: 1 }}>
                  <ArrowBackIcon data-testid='arrow-back' />
                </IconButton>
              )}
              <Typography variant='titleMedium'>{title}</Typography>
            </Stack>
            {lessonCompleted ? feedbackForm : children}
            <GoogleClassroomStudentValidator />
          </PlayerContainer>
        </FullScreen>
      </CssBaseline>
    </ThemeProvider>
  );
}

export const Player = ({ user }: { user: User | null }) => (
  <PanelAreaProvider>
    <PlayerInternal user={user} />
  </PanelAreaProvider>
);
